import { PathValue, FieldValues, Path, useFormContext, get } from 'react-hook-form';
import { FormComponentProps } from '../types';
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import React from 'react';
import { VirtualScrollbar } from './VirtualScrollbar';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

interface FormDropDownProps<T> extends FormComponentProps<T> {
  placeholder: string;
  mandatory?: boolean;
  title: string;
  elementsForDisplay: { title: string; value: PathValue<T, Path<T>> }[];
  classProps?: string;
  hideErrorControl?: boolean;
}

export const FormSearchDropdown = <T extends FieldValues>(props: FormDropDownProps<T>) => {
  const inputRef = React.createRef<HTMLInputElement>();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const listRef = useRef(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const {
    formState: { errors },
    setValue,
    watch,
    setFocus,
    register
  } = useFormContext<T>();

  const inputProps = register<Path<T>>(props.name);
  const fieldValue = watch(props.name);

  const errorForName = useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);

  const [searchTerm, setSearchTerm] = useState<string | undefined>('');

  const selectedValue = useMemo(() => {
    const element = props.elementsForDisplay.find(element => element.value == fieldValue);
    if (element) {
      return element.title;
    }
    return props.placeholder;
  }, [fieldValue, props.elementsForDisplay, props.placeholder]);

  const debouncedSearchTerm = useDebounce(searchTerm, 150);

  const handleSearchStringChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredElements = useMemo(() => {
    if (!debouncedSearchTerm) {
      return props.elementsForDisplay;
    }
    setCurrentItemIndex(0);
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      align: 'start',
      behavior: 'smooth'
    });
    return props.elementsForDisplay
      .filter(element => element.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      .map(el => ({
        title: el.title,
        value: el.value as any
      }));
  }, [debouncedSearchTerm, props.elementsForDisplay]);

  const [inputIsFocused, setInputIsFocused] = useState(false);

  const onElementSelect = (index: number) => {
    if (index <= filteredElements.length) {
      // setSearchTerm(filteredElements[index].title);
      setValue(props.name, filteredElements[index].value);
    }
    setInputIsFocused(false);
  };

  const onInputFocus = () => {
    if (props.readonly) return;
    setSearchTerm('');
    setInputIsFocused(true);
    inputRef.current?.focus();
    const newState = !inputIsFocused;

    if (newState) {
      setTimeout(() => {
        if (!virtuosoRef.current) return;
        virtuosoRef.current.scrollIntoView({
          index: currentItemIndex,
          behavior: 'auto'
        });
      }, 50);
    }
  };

  const onInputClick = () => {
    if (props.readonly) return;
    setFocus(props.name);
  };

  useEffect(() => {
    if (inputIsFocused) {
      inputRef.current?.focus();
    }
  }, [inputIsFocused, inputRef]);

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };

  const renderElement = (index: number, element: { title: string; value: PathValue<T, Path<T>> }) => (
    <div
      key={index}
      onMouseEnter={() => setCurrentItemIndex(index)}
      className={`bg-interactive-bold-green-db-12 ${currentItemIndex === index ? 'bg-interactive-light-teal-100' : ''}`}
      onMouseDown={() => onElementSelect(index)}
    >
      <div className={`pl-3 pt-3 pb-3 ${element.title === selectedValue ? 'border-l-4 border-solid-bold-green' : ''}`}>
        <p>{element.title}</p>
      </div>
    </div>
  );

  const keyDownCallback = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      let nextIndex: number | null = null;
      if (event.key === 'ArrowDown') {
        nextIndex = currentItemIndex + 1;
        if (nextIndex >= filteredElements.length) {
          nextIndex = 0;
        }
      } else if (event.key === 'ArrowUp') {
        nextIndex = currentItemIndex - 1;
        if (nextIndex < 0) {
          nextIndex = filteredElements.length - 1;
        }
      } else if (event.key === 'Enter') {
        onElementSelect(currentItemIndex);
        event.preventDefault();
        return;
      }

      if (nextIndex !== null && virtuosoRef.current !== null) {
        virtuosoRef.current.scrollIntoView({
          index: nextIndex,
          behavior: 'smooth',
          done: () => {
            if (nextIndex === null) return;
            setCurrentItemIndex(nextIndex);
          }
        });
        event.preventDefault();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentItemIndex, setCurrentItemIndex, virtuosoRef, filteredElements.length]
  );

  const scrollerRef = useCallback(
    (element: any) => {
      if (element && inputIsFocused) {
        window.addEventListener('keydown', keyDownCallback as any);
        listRef.current = element;
      } else {
        window.removeEventListener('keydown', keyDownCallback as any);
      }
    },
    [inputIsFocused, keyDownCallback]
  );

  const backgroundColour = () => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (errorForName) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  };

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (errorForName) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (errorForName) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  return (
    <div className={`w-full ${props.classProps ?? ''} relative`}>
      <div
        className={`${backgroundColour()} ${borderColour()} border-b-2 w-full cursor-pointer pt-1`}
        tabIndex={props.readonly ? -1 : 0}
        onClick={onInputClick}
        onBlur={() => setInputIsFocused(false)}
        onFocus={onInputFocus}
        ref={inputProps.ref}
      >
        <div className="flex flex-row justify-between ml-3 pr-3 py-1">
          <label className={`text-xs cursor-pointer ${labelTextColour()}`}>{props.title}</label>
          {props.mandatory && !props.readonly && <label className={`text-xs ${labelTextColour()} block`}>*</label>}
        </div>
        <div className="cursor-pointer pl-3 pr-5 pb-1 h-7 flex flex-row justify-between">
          <div className="w-full">
            <p className={`${backgroundColour()} w-full truncate focus:outline-none ${selectedValue === props.placeholder ? 'text-secondary-deep-blue-30' : ''}`}>{selectedValue}</p>
          </div>
          {!props.readonly && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute right-[20px] -translate-y-2 translate-x-1">
              <path d="M12 15L7 10H17L12 15Z" className={errorForName ? 'fill-feedback-red' : 'fill-solid-bold-green'} />
            </svg>
          )}
        </div>
      </div>
      {inputIsFocused && (
        <div className={`z-10 absolute w-full bg-interactive-bold-green-db-12 border-solid-bold-green border-[1px]`}>
          <div className="flex flex-row mx-2 pt-1 border-b-[1px] border-b-solid-bold-green">
            <img src={'/search.svg'} alt={'Search'} className="scale-75" />
            <input
              tabIndex={-1}
              value={searchTerm}
              onChange={handleSearchStringChange}
              type="text"
              className={`w-full pl-3 pr-5 py-1 ${backgroundColour()} focus:outline-none bg-origin-content bg-no-repeat bg-[length:12px_7px] bg-[right_bottom_20px]`}
              onBlur={() => setInputIsFocused(false)}
              onFocus={() => setInputIsFocused(true)}
              ref={inputRef}
            />
          </div>
          <div className="resize-y overflow-hidden" style={{ height: filteredElements.length <= 5 ? filteredElements.length * 50 : 300 }}>
            <Virtuoso
              ref={virtuosoRef}
              tabIndex={-1}
              data={filteredElements}
              itemContent={renderElement}
              scrollerRef={scrollerRef}
              style={{ height: '100%' }}
              components={{
                Scroller: VirtualScrollbar
              }}
            />
          </div>
        </div>
      )}
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
